import { useState, forwardRef, useCallback } from 'react';
import { Combobox } from '@headlessui/react';
import Avatar from '../../features/avatar/Avatar';
import { twMerge } from 'tailwind-merge';
import useIsMobileView from '@/hooks/useIsMobileView';
import { debounce } from 'lodash';

interface SelectProps<T> {
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  options: T[];
  labelKey?: keyof T;
  placeholder?: string;
  placeholderClassName?: string;
  onChange?: (value: T | null) => void;
  hideInput?: boolean;
  defaultSelectedLabelKey?: string;
  containerClassName?: string;
  allowNewValues?: boolean;
}

const Select = forwardRef<HTMLSelectElement, SelectProps<any>>(
  ({
    disabled = false,
    options,
    labelKey,
    fullWidth = true,
    placeholder,
    placeholderClassName,
    onChange,
    defaultSelectedLabelKey,
    containerClassName,
    allowNewValues = false,
  }) => {
    const isMobileView = useIsMobileView();
    const initialSelectedOption = getInitialSelectedOption(options, labelKey, defaultSelectedLabelKey);
    const [selectedOption, setSelectedOption] = useState(initialSelectedOption);
    const [localOptions, setLocalOptions] = useState(options);
    const [query, setQuery] = useState('');

    const filteredOptions = filterOptions(localOptions, query, labelKey);

    const handleSelectChange = useCallback(
      (value: any) => {
        setSelectedOption(value);
        onChange?.(value);
      },
      [onChange],
    );

    const handleInputKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          const inputValue = (event.target as HTMLInputElement).value.trim();
          const matchingOption = findMatchingOption(localOptions, inputValue, labelKey);
          if (allowNewValues && inputValue && !matchingOption) {
            addNewOption(inputValue);
            event.preventDefault();
          } else if (!matchingOption) {
            setSelectedOption(null); // Clear the selection
            setQuery(''); // Clear the query
            setLocalOptions(options); // Reset options when no match is found
          }
        }
      },
      [localOptions, labelKey, allowNewValues, options],
    );

    const handleInputBlur = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const queryValue = event.target.value.trim();
        if (queryValue) {
          const matchingOption = findMatchingOption(localOptions, queryValue, labelKey);
          if (allowNewValues && !matchingOption) {
            addNewOption(queryValue);
          } else if (matchingOption) {
            handleSelectChange(matchingOption);
          } else {
            setSelectedOption(null); // Clear the selection
            setQuery(''); // Clear the query
            setLocalOptions(options); // Reset options when no match is found
          }
        } else {
          setSelectedOption(null); // Clear the selection
          setQuery(''); // Clear the query if empty
          setLocalOptions(options); // Reset options if input is empty
        }
      },
      [allowNewValues, handleSelectChange, labelKey, options],
    );

    const addNewOption = useCallback(
      (inputValue: string) => {
        const newOption = labelKey ? { [labelKey]: inputValue } : inputValue;
        setLocalOptions((prevOptions) => [...prevOptions, newOption]);
        handleSelectChange(newOption);
      },
      [handleSelectChange, labelKey],
    );

    const debouncedSetQuery = useCallback(
      debounce((value: string) => {
        setQuery(value);
        if (!value) {
          setLocalOptions(options); // Reset options when query is cleared
        } else {
          setLocalOptions(filterOptions(options, value, labelKey));
        }
      }, 300),
      [options, labelKey],
    );

    const debouncedHandleInputChange = useCallback(
      debounce((inputValue: string) => {
        if (allowNewValues && inputValue.trim()) {
          const matchingOption = findMatchingOption(localOptions, inputValue, labelKey);
          if (!matchingOption) {
            addNewOption(inputValue);
          } else {
            handleSelectChange(matchingOption);
          }
        }
      }, 1000),
      [localOptions, labelKey, allowNewValues, addNewOption, handleSelectChange],
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSetQuery(e.target.value);
      if (allowNewValues) {
        debouncedHandleInputChange(e.target.value);
      }
    };

    const handleButtonClick = () => {
      if (!query || filteredOptions.length === 0) {
        setQuery('');
        setLocalOptions(options); // Reset options if button is clicked and query is empty or no matching options are found
      }
    };

    return (
      <div
        className={twMerge(
          'relative rounded-[10px] border-[1px] border-ph-white-10',
          `${fullWidth ? 'w-full' : 'inline-block'}`,
          containerClassName,
        )}
      >
        <Combobox value={selectedOption} onChange={handleSelectChange} disabled={disabled}>
          <div className="relative w-full">
            <Combobox.Input
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              onChange={handleInputChange}
              displayValue={(option) => getDisplayValue(option, labelKey)}
              className={twMerge(
                'h-12 w-full overflow-hidden rounded-lg border-transparent bg-ph-white-3 py-3 pl-3 text-sm font-normal tracking-wide text-white !outline-none focus:border-transparent focus:ring-0',
                placeholderClassName,
              )}
              placeholder={placeholder}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-5" onClick={handleButtonClick}>
              <DropdownIcon />
            </Combobox.Button>
          </div>
          <Combobox.Options
            className={twMerge(
              'scrollbar-phenom absolute z-[11] mt-1 w-full overflow-y-auto rounded-b-lg bg-ph-gray-field py-3 text-sm font-normal text-white',
              isMobileView ? 'max-h-[50dvh]' : 'max-h-60',
            )}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => <ComboboxOption key={index} option={option} labelKey={labelKey} />)
            ) : (
              <div className="p-2 text-center text-sm text-gray-500">No options available</div>
            )}
          </Combobox.Options>
        </Combobox>
      </div>
    );
  },
);

const getInitialSelectedOption = (
  options: any[],
  labelKey: string | number | symbol | undefined,
  defaultSelectedLabelKey: string | undefined,
) => {
  return defaultSelectedLabelKey && labelKey
    ? options.find((option) => option[labelKey] === defaultSelectedLabelKey)
    : null;
};

const findMatchingOption = (options: any[], value: string, labelKey: string | number | symbol | undefined) => {
  return options.find((option) => (labelKey ? option[labelKey] === value : option === value));
};

const getDisplayValue = (option: any, labelKey: string | number | symbol | undefined) => {
  return option && labelKey ? option[labelKey] : option || '';
};

const filterOptions = (options: any[], query: string, labelKey: string | number | symbol | undefined) => {
  return options.filter((option) =>
    labelKey
      ? String(option[labelKey]).toLowerCase().includes(query.toLowerCase())
      : String(option).toLowerCase().includes(query.toLowerCase()),
  );
};

const DropdownIcon = () => (
  <svg
    className="h-5 w-5 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

const ComboboxOption = ({ option, labelKey }: { option: any; labelKey?: string | number | symbol }) => (
  <Combobox.Option
    value={option}
    className="relative flex cursor-default select-none items-center gap-2 bg-ph-gray-field py-2 pl-2 text-sm font-normal text-white hover:cursor-pointer hover:bg-ph-gray-evening"
  >
    {labelKey ? (
      <>
        <Avatar
          smallLoadingSpinner
          avatar2D
          round={false}
          userId={option.id}
          className="h-8 w-8 rounded-md border border-ph-primary-violet"
        />
        <span>{option[labelKey]}</span>
      </>
    ) : (
      <p>{option}</p>
    )}
  </Combobox.Option>
);

export default Select;
